import React from "react";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencilAlt } from "@fortawesome/free-solid-svg-icons";
import { colors } from "utils";
import { trackClickedButton } from "utils/analytics";
import { matchPageName } from "hooks/usePageViewTracker";
import { SelectDropdown } from "components/shared/FilterDropdowns/FilterDropdowns";
import { classDisplayName } from "components/shared/ClassroomFilter/groupClassesBySchoolYear";

// Rename-class pencil. Shared by both render paths so the stacked selector keeps
// the affordance the plain dropdown has.
const EditPencil = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    title="Rename class"
    aria-label="Rename class"
    style={{
      alignItems: "center",
      backgroundColor: "transparent",
      border: "none",
      color: colors.nightlyWoods500,
      cursor: "pointer",
      display: "flex",
      padding: "8px"
    }}
  >
    <FontAwesomeIcon icon={faPencilAlt} style={{ fontSize: "16px" }} />
  </button>
);

export const ClassroomFilter = ({
  classes,
  selectedClassId,
  onClassChange,
  showEditButton = true,
  onEditClick,
  inline = false,
  variant,
}) => {
  const location = useLocation();
  if (!classes || classes.length === 0) {
    return null;
  }

  const stacked = variant === "stacked";
  const selectedClass = classes.find(c => c.id === selectedClassId);
  const displayName = classDisplayName(selectedClass) || "Select a class";

  const handleSelect = (clsId) => {
    trackClickedButton("Class Filter", {
      page_name: matchPageName(location.pathname),
    });
    onClassChange(clsId);
  };

  // Stacked variant ("Class" label above the control) reuses the shared
  // SelectDropdown so it is identical to the class selector on the lesson list
  // (LessonGroupsList) — no react-bootstrap toggle/menu divergence.
  if (stacked) {
    return (
      <div style={{ alignItems: "flex-end", display: "flex", gap: 8 }}>
        <div style={{ maxWidth: 200, width: "100%" }}>
          <SelectDropdown
            label="Class"
            value={selectedClassId}
            displayText={displayName}
            options={classes.map((cls) => ({ value: cls.id, label: cls.name, school_year: cls.school_year }))}
            groupBySchoolYear
            onChange={handleSelect}
          />
        </div>
        {showEditButton && onEditClick && <EditPencil onClick={onEditClick} />}
      </div>
    );
  }

  const containerStyle = inline
    ? { alignItems: "center", display: "flex", gap: "16px" }
    : {
        alignItems: "center",
        backgroundColor: "white",
        borderRadius: "12px",
        boxShadow: "0px 1px 2px 0px rgba(14, 26, 49, 0.05)",
        display: "flex",
        gap: "16px",
        padding: "16px 24px",
      };

  return (
    <div style={containerStyle}>
      <span style={{ fontSize: "16px", fontWeight: "600", color: colors.nightlyWoods950 }}>
        Class:
      </span>
      <div style={{ minWidth: 200 }}>
        <SelectDropdown
          ariaLabel="Class"
          value={selectedClassId}
          displayText={displayName}
          options={classes.map((cls) => ({ value: cls.id, label: cls.name, school_year: cls.school_year }))}
          groupBySchoolYear
          onChange={handleSelect}
        />
      </div>
      {!inline && showEditButton && onEditClick && <EditPencil onClick={onEditClick} />}
    </div>
  );
};
